import {
  fmt,
  INTL_TAG,
  LOCALES,
  messagesFor,
  plural,
  type Locale,
  type Messages,
  type Params,
  type Plural,
} from '@indusketch/i18n';
import { getNumberLocale, setNumberLocale } from './units.js';

export { fmt as fillText };

export type ModelTexts = Messages['model'];

/** Language of catalog names, warnings and labels; the app sets it together with the number format. */
let textLocale: Locale = 'de';

export function setTextLocale(locale: Locale): void {
  textLocale = locale;
}

export const getTextLocale = (): Locale => textLocale;

/** Texts of the domain model in the current (or a given) language. */
export const modelTexts = (locale: Locale = textLocale): ModelTexts => messagesFor(locale).model;

/**
 * Runs `fn` with another text and number language, e.g. to build a report in the language
 * chosen for it. `fn` must be synchronous: both are module-wide settings.
 */
export function withTextLocale<T>(locale: Locale, fn: () => T): T {
  const previous = textLocale;
  const previousNumbers = getNumberLocale();
  textLocale = locale;
  setNumberLocale(INTL_TAG[locale]);
  try {
    return fn();
  } finally {
    textLocale = previous;
    setNumberLocale(previousNumbers);
  }
}

/** Reason a command was rejected, in the current text language. */
export const errorText = (key: keyof ModelTexts['errors'], params?: Params): string =>
  fmt(modelTexts().errors[key], params);

/** Name of an undoable action in the current text language. */
export const commandText = (key: keyof ModelTexts['commands'], params?: Params): string =>
  fmt(modelTexts().commands[key], params);

/** Name for a new element in the current text language. */
export const defaultName = (key: keyof ModelTexts['defaults'], params?: Params): string =>
  fmt(modelTexts().defaults[key], params);

/** Plural form for `count` in the current text language. */
export const pluralText = (forms: Plural, count: number, params?: Params): string =>
  plural(textLocale, forms, count, params);

/** The same text in every language, to recognise names that were never changed by the user. */
export const inEveryLocale = (pick: (t: ModelTexts) => string): readonly string[] =>
  LOCALES.map((l) => pick(modelTexts(l)));
